/**
 * Hook to use the image processing Web Worker.
 */

import { useState, useCallback, useRef } from 'react';
import type { PipelineConfig, PipelineResult, PipelineProgress } from './pipeline';
import type { WorkerResponse } from './worker';

interface UseImageWorkerResult {
  readonly isProcessing: boolean;
  readonly progress: PipelineProgress | null;
  readonly result: PipelineResult | null;
  readonly error: string | null;
  readonly processImage: (imageData: ImageData, config: PipelineConfig) => void;
  readonly reset: () => void;
}

export function useImageWorker(): UseImageWorkerResult {
  const [isProcessing, setIsProcessing] = useState(false);
  const [progress, setProgress] = useState<PipelineProgress | null>(null);
  const [result, setResult] = useState<PipelineResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const workerRef = useRef<Worker | null>(null);

  const processImage = useCallback((imageData: ImageData, config: PipelineConfig) => {
    // Terminate any existing worker
    workerRef.current?.terminate();

    setIsProcessing(true);
    setProgress(null);
    setResult(null);
    setError(null);

    const worker = new Worker(
      new URL('./worker.ts', import.meta.url),
      { type: 'module' },
    );
    workerRef.current = worker;

    worker.onmessage = (e: MessageEvent<WorkerResponse>) => {
      const msg = e.data;
      switch (msg.type) {
        case 'progress':
          setProgress(msg.data);
          break;
        case 'result':
          setResult(msg.data);
          setIsProcessing(false);
          worker.terminate();
          workerRef.current = null;
          break;
        case 'error':
          setError(msg.error);
          setIsProcessing(false);
          worker.terminate();
          workerRef.current = null;
          break;
      }
    };

    worker.onerror = (e) => {
      setError(e.message ?? 'Worker error');
      setIsProcessing(false);
      worker.terminate();
      workerRef.current = null;
    };

    worker.postMessage({ type: 'process', imageData, config });
  }, []);

  const reset = useCallback(() => {
    workerRef.current?.terminate();
    workerRef.current = null;
    setIsProcessing(false);
    setProgress(null);
    setResult(null);
    setError(null);
  }, []);

  return { isProcessing, progress, result, error, processImage, reset };
}
